// POS หน้าร้าน — BO helpers (#443).
// Spec: mdf-project docs/specs/2026-10-10-pos-module-design.md §5, §7.
// Vue-free so jest can pin them (this repo cannot mount .vue in jest).
// House rules: nothing here throws; junk input yields safe output.

/** Max inclusive span of every report/bill-list date filter (§5.1). */
export const POS_MAX_RANGE_DAYS = 92;

/** Rows the API returns at most with export=1 (R28). */
export const POS_EXPORT_MAX_ROWS = 20000;

const pad = (n) => (n < 10 ? "0" + n : "" + n);

/** Local calendar date as YYYY-MM-DD. */
export function posYmd(d) {
	const dt = d instanceof Date ? d : new Date(d);
	if (isNaN(dt.getTime())) return "";
	return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/** Parse YYYY-MM-DD as a UTC day number (or NaN). */
function dayNumber(ymd) {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
	if (!m) return NaN;
	const t = Date.UTC(+m[1], +m[2] - 1, +m[3]);
	const back = new Date(t);
	if (back.getUTCFullYear() !== +m[1] || back.getUTCMonth() !== +m[2] - 1 || back.getUTCDate() !== +m[3]) return NaN;
	return Math.round(t / 86400000);
}

/** Inclusive number of days between two YYYY-MM-DD dates, or NaN. */
export function posRangeDays(from, to) {
	const a = dayNumber(from);
	const b = dayNumber(to);
	if (isNaN(a) || isNaN(b)) return NaN;
	return b - a + 1;
}

/**
 * Validate a [from, to] pair. Returns "" when fine, else the i18n key of the
 * message: pos.filter.range_invalid or pos.filter.range_too_long (> 92 days).
 */
export function posValidateRange(from, to) {
	const n = posRangeDays(from, to);
	if (isNaN(n) || n < 1) return "pos.filter.range_invalid";
	if (n > POS_MAX_RANGE_DAYS) return "pos.filter.range_too_long";
	return "";
}

/**
 * Build the §5.1 query params from the filter bar state.
 * eventId: "" / null / undefined = all; 0 = default set; > 0 = that event.
 */
export function posReportParams(filter) {
	const f = filter || {};
	const range = Array.isArray(f.range) ? f.range : [];
	const p = { from: range[0] || "", to: range[1] || "", include_test: f.includeTest ? 1 : 0 };
	if (f.eventId !== "" && f.eventId !== null && f.eventId !== undefined && !isNaN(Number(f.eventId))) {
		p.event_id = Number(f.eventId);
	}
	if (f.deviceId !== "" && f.deviceId !== null && f.deviceId !== undefined && Number(f.deviceId) > 0) {
		p.device_id = Number(f.deviceId);
	}
	return p;
}

/** Money as "1,692.00" (THB, 2 decimals). Non-numbers → "0.00". */
export function posMoney(v) {
	const n = Number(v);
	if (!isFinite(n)) return "0.00";
	const neg = n < 0;
	const satang = Math.round(Math.abs(n) * 100);
	const whole = Math.floor(satang / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return (neg ? "-" : "") + whole + "." + pad(satang % 100);
}

/** Integer with thousands separators. */
export function posInt(v) {
	const n = Math.round(Number(v) || 0);
	return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * integer = true (counts) keeps every tick a whole number.
 * A "nice" linear scale for a bar chart drawn to scale: the axis maximum is the
 * smallest 1/2/2.5/5×10^k step multiple ≥ max, with `ticks` intervals.
 * Returns { max, step, ticks: [0, step, …, max] }. max ≤ 0 → { max: 1, … }.
 */
export function posNiceScale(maxValue, ticks = 4, integer = false) {
	const n = Math.max(1, Math.floor(Number(ticks) || 4));
	const m = Number(maxValue);
	if (!isFinite(m) || m <= 0) {
		return { max: 1, step: 1 / n, ticks: Array.from({ length: n + 1 }, (_, i) => i / n) };
	}
	const raw = m / n;
	const mag = Math.pow(10, Math.floor(Math.log10(raw)));
	let factors = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((f) => f * mag);
	if (integer) factors = factors.map((f) => Math.max(1, f)).filter((f) => Number.isInteger(f));
	const nice = factors.find((s) => s * n >= m - 1e-9) || Math.max(integer ? 1 : 0, 10 * mag);
	const step = nice;
	const out = [];
	for (let i = 0; i <= n; i++) out.push(Math.round(step * i * 1e6) / 1e6);
	return { max: out[n], step, ticks: out };
}

/** Bar length as a percentage of the scale max, clamped to [0, 100]. */
export function posBarPct(value, scaleMax) {
	const v = Number(value);
	const m = Number(scaleMax);
	if (!isFinite(v) || !isFinite(m) || m <= 0 || v <= 0) return 0;
	return Math.min(100, (v / m) * 100);
}

/**
 * Rows for XLSX.utils.json_to_sheet: one object per row whose keys are the
 * visible (Thai) column headers, in column order. columns = [{ label, value }]
 * where value is a field name or a function(row).
 */
export function posExportRows(columns, rows) {
	const cols = Array.isArray(columns) ? columns : [];
	return (Array.isArray(rows) ? rows : []).map((r) => {
		const o = {};
		cols.forEach((c) => {
			const v = typeof c.value === "function" ? c.value(r) : r ? r[c.value] : undefined;
			o[c.label] = v === undefined || v === null ? "" : v;
		});
		return o;
	});
}

/** File name of an Excel export: pos-<report>-<from>_<to>.xlsx (§5.6). */
export function posExportFileName(report, from, to) {
	return `pos-${report}-${from}_${to}.xlsx`;
}

/** Is this axios error the fail-soft 503 pos_not_installed? */
// posNotInstalledKind → "phase1" (pos_not_installed), "phase3"
// (pos_phase3_not_installed, spec phase 3 R-S) or "" for any other answer.
export function posNotInstalledKind(err) {
	const r = err && err.response;
	if (!r || r.status !== 503 || !r.data) return "";
	if (r.data.Msg === "pos_not_installed") return "phase1";
	if (r.data.Msg === "pos_phase3_not_installed") return "phase3";
	return "";
}
export function posIsNotInstalled(err) {
	return posNotInstalledKind(err) !== "";
}

/**
 * User-facing message for a POS API error: pos.errors.<code> when the code is
 * a POS code with a translation, else the caller's fallback (generic text).
 * t is the i18n translate function, te the "exists" check (optional).
 */
export function posErrorMessage(err, t, te, fallback) {
	const r = err && err.response;
	const code = r && r.data && typeof r.data.Msg === "string" ? r.data.Msg : "";
	const kind = posNotInstalledKind(err);
	if (kind === "phase3") return t ? t("pos.not_installed_phase3") : "pos_phase3_not_installed";
	if (kind) return t ? t("pos.not_installed") : "pos_not_installed";
	if (code.indexOf("pos_") === 0) {
		const key = `pos.errors.${code}`;
		if (!te || te(key)) return t ? t(key) : key;
	}
	if (fallback) return fallback;
	return t ? t("pos.errors.generic") : "error";
}

/** Device-code input normaliser (uppercase, A–Z 0–9 only, max 10). */
export function posNormalizeDeviceCode(s) {
	return String(s || "")
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, "")
		.slice(0, 10);
}

/** ^[A-Z0-9]{2,10}$ (R13 / B2). */
export function posValidDeviceCode(s) {
	return /^[A-Z0-9]{2,10}$/.test(String(s || ""));
}

/** "mm:ss" until expiresAt ("YYYY-MM-DD HH:mm:ss" Asia/Bangkok) from now (ms). */
export function posCountdown(expiresAt, nowMs) {
	const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(String(expiresAt || ""));
	if (!m) return "00:00";
	// Asia/Bangkok is UTC+7 with no DST.
	const t = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4] - 7, +m[5], +m[6]);
	const left = Math.max(0, Math.floor((t - (nowMs === undefined ? Date.now() : nowMs)) / 1000));
	return `${pad(Math.floor(left / 60))}:${pad(left % 60)}`;
}

/** Retail sum and saving of a bundle form: items [{qty, retail_price}], price. */
export function posBundleTotals(items, price) {
	let retail = 0;
	(Array.isArray(items) ? items : []).forEach((it) => {
		const q = Math.max(0, Math.floor(Number(it && it.qty) || 0));
		retail += Math.round((Number(it && it.retail_price) || 0) * 100) * q;
	});
	const p = Math.round((Number(price) || 0) * 100);
	return { retail: retail / 100, saving: (retail - p) / 100 };
}

/** Does a logo data URL pass B25 (png/jpeg data URL, ≤ 200 KB)? */
export function posLogoOk(dataUrl) {
	const s = String(dataUrl || "");
	if (s === "") return true;
	return /^data:image\/(png|jpeg);base64,/.test(s) && s.length <= 200 * 1024;
}

/** Receipt-model sample for the settings preview (shape of §4.2 <ReceiptModel>). */
export function posSampleReceiptModel(setting) {
	const s = setting || {};
	return {
		header: {
			logo_data_url: s.logo_data_url || "",
			shop_name: s.shop_name || "",
			address: s.address || "",
			phone: s.phone || "",
			header_note: s.header_note || ""
		},
		title: "ใบเสร็จรับเงิน",
		bill_no: "POS01-2610-00042",
		datetime_text: "10/10/2569 14:05",
		device_code: "POS01",
		seller_name: "สมหญิง ใจดี",
		event_name: "Siam Paragon Pop-up",
		customer: {
			name: "คุณเอ",
			phone_masked: "081-xxx-5678",
			address_text: s.show_customer_address === false ? "" : "99/1 แขวงคลองเตยเหนือ เขตวัฒนา กรุงเทพมหานคร 10110"
		},
		lines: [
			{ name: "เซรั่มหน้าใส 30ml", qty: 2, unit_price_text: "490.00", amount_text: "980.00", components: [] },
			{ name: "เซ็ตคู่หน้าใส", qty: 1, unit_price_text: "899.00", amount_text: "899.00", components: [{ name: "เซรั่มหน้าใส 30ml", qty: 2 }] }
		],
		subtotal_text: "1,879.00",
		discount_label: "ส่วนลด 10%",
		discount_text: "-187.00",
		total_text: "1,692.00",
		payment: { method_label: "เงินสด", received_text: "2,000.00", change_text: "308.00" },
		footer: { text: s.footer_text || "", qr_text: s.footer_qr_text || "", qr_caption: s.footer_qr_caption || "" },
		flags: { reprint: false, voided: false }
	};
}

/**
 * Label of an event group. The API sends the device default set (event 0)
 * with an empty name; it is translated with pos.filter.event_default.
 * eventId null/undefined = not an event grouping (name returned as is).
 */
export function posEventLabel(eventId, name, t) {
	if (eventId !== null && eventId !== undefined && Number(eventId) === 0) {
		return t ? t("pos.filter.event_default") : "pos.filter.event_default";
	}
	return name || "";
}

// ---------------------------------------------------------------------------
// phase 3 (#445)
// ---------------------------------------------------------------------------

/** Scale (w, h) so the longer side is at most `max` (never upscales). */
export function posFitSize(w, h, max) {
	const W = Number(w) || 0;
	const H = Number(h) || 0;
	if (W <= 0 || H <= 0) return { w: 0, h: 0 };
	const scale = Math.min(1, max / Math.max(W, H));
	return { w: Math.max(1, Math.round(W * scale)), h: Math.max(1, Math.round(H * scale)) };
}

/** Approximate decoded size of a base64 data URL (bytes). */
export function posDataUrlBytes(dataUrl) {
	const s = String(dataUrl || "");
	const i = s.indexOf(",");
	const b64 = (i < 0 ? s : s.slice(i + 1)).replace(/=+$/, "");
	return Math.floor((b64.length * 3) / 4);
}

export const POS_PRODUCT_IMAGE_MAX_SIDE = 800;
export const POS_PRODUCT_IMAGE_MAX_BYTES = 1024 * 1024;
export const POS_QR_IMAGE_MAX_SIDE = 600;
export const POS_QR_IMAGE_MAX_BYTES = 300 * 1024;

/**
 * Read an image File and re-encode it in the browser: longest side ≤ maxSide,
 * `type` "image/jpeg" (quality) or "image/png". Resolves a data URL.
 */
export function posResizeImageFile(file, maxSide, type, quality) {
	return new Promise((resolve, reject) => {
		if (!file || !/^image\/(png|jpe?g)$/.test(file.type || "")) {
			reject(new Error("pos_image_invalid"));
			return;
		}
		const reader = new FileReader();
		reader.onerror = () => reject(new Error("pos_image_invalid"));
		reader.onload = () => {
			const img = new Image();
			img.onerror = () => reject(new Error("pos_image_invalid"));
			img.onload = () => {
				const { w, h } = posFitSize(img.width, img.height, maxSide);
				const canvas = document.createElement("canvas");
				canvas.width = w;
				canvas.height = h;
				const ctx = canvas.getContext("2d");
				if (type === "image/jpeg") {
					ctx.fillStyle = "#fff";
					ctx.fillRect(0, 0, w, h);
				}
				ctx.drawImage(img, 0, 0, w, h);
				resolve(canvas.toDataURL(type, quality));
			};
			img.src = reader.result;
		};
		reader.readAsDataURL(file);
	});
}

/** URL of a POS product image on the BO file server (cookie-authenticated). */
export function posProductImageUrl(base, filename) {
	const f = String(filename || "").trim();
	if (!f) return "";
	return `${base || ""}/files/pos_products/${f}`;
}

/** Σ qty of WMS lines; and the "n lines · q pieces" counters. */
export function posWmsTotals(items) {
	const list = Array.isArray(items) ? items : [];
	return { lines: list.length, pieces: list.reduce((s, i) => s + (Number(i && i.qty) || 0), 0) };
}

/** B41/B43 body from the dialog form (image only when a new one was chosen). */
export function posProductBody(form) {
	const f = form || {};
	const body = {
		name: String(f.name || "").trim(),
		code: String(f.code || "").trim().toUpperCase(),
		price: Number(f.price),
		status: f.status === 0 ? 0 : 1,
		note: String(f.note || ""),
		items: (f.items || []).filter((i) => i && i.wms_product_id).map((i) => ({ wms_product_id: i.wms_product_id, qty: Number(i.qty) || 0 }))
	};
	if (f.image_data_url) body.image_data_url = f.image_data_url;
	if (f.image_remove && !f.image_data_url) body.image_remove = true;
	return body;
}

/** Client checks mirroring B41 §3.4 (server re-validates): list of bad fields. */
export function posProductInvalid(form) {
	const b = posProductBody(form);
	const bad = [];
	if (b.name.length < 1 || b.name.length > 150) bad.push("name");
	if (b.code && !/^[A-Z0-9_-]{1,30}$/.test(b.code)) bad.push("code");
	const cents = b.price * 100;
	const twoDp = Math.abs(Math.round(cents) - cents) <= 1e-7 * Math.max(1, Math.abs(cents));
	if (!(b.price > 0) || b.price > 9999999.99 || !twoDp) bad.push("price");
	const ids = b.items.map((i) => i.wms_product_id);
	if (b.items.length < 1 || b.items.length > 20 || new Set(ids).size !== ids.length) bad.push("items");
	if (b.items.some((i) => !(i.qty >= 1 && i.qty <= 999))) bad.push("items");
	return bad;
}

/** P1 member-id text: 4 digits (or more), "" for none. */
export function posMidText(n) {
	const v = Number(n);
	if (!v || v < 1) return "";
	const s = String(Math.floor(v));
	return s.length >= 4 ? s : `0000${s}`.slice(-4);
}

/** Passcode shape (6 digits) and weakness (twin of posPasscodeWeak). */
export function posPasscodeValid(s) {
	return typeof s === "string" && /^\d{6}$/.test(s);
}
export function posPasscodeWeak(s) {
	if (!posPasscodeValid(s)) return false;
	let same = true;
	let up = true;
	let down = true;
	for (let i = 1; i < 6; i++) {
		const d = s.charCodeAt(i) - s.charCodeAt(i - 1);
		if (d !== 0) same = false;
		if (d !== 1) up = false;
		if (d !== -1) down = false;
	}
	return same || up || down;
}

/** B1 passcode_state → i18n key. */
export function posPasscodeStateKey(state) {
	const m = { set: "pos.device.passcode_set", cooldown: "pos.device.passcode_cooldown", hard_locked: "pos.device.passcode_hard_locked" };
	return m[state] || "pos.device.passcode_none";
}

// §14 idle-lock choices (seconds; 0 = never auto-lock), twin of bo-api posLockIdleValid.
export const POS_LOCK_IDLE_OPTIONS = [300, 600, 900, 0];
export function posLockIdleValid(v) {
	return POS_LOCK_IDLE_OPTIONS.indexOf(Number(v)) !== -1 && v !== null && v !== "" && v !== undefined;
}
export function posLockIdleKey(v) {
	const m = { 300: "pos.device.lock_idle_300", 600: "pos.device.lock_idle_600", 900: "pos.device.lock_idle_900", 0: "pos.device.lock_idle_0" };
	return posLockIdleValid(v) ? m[Number(v)] : "pos.device.lock_idle_other";
}
